import type { KyInstance } from 'ky';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import type { SelfSignedTradingSessionProof } from '@/lib/api/tradingSession/types';

import {
    onboardingConfigSchema,
    onboardingStatusSchema,
    onboardingWalletStartSchema,
} from './schemas';
import type {
    OnboardingConfig,
    OnboardingStatus,
    OnboardingWalletStart,
} from './types';

export async function fetchOnboardingConfig(
    api: KyInstance,
): Promise<OnboardingConfig> {
    return onboardingConfigSchema.parse(
        await api.get('speed/onboarding/config').json(),
    );
}

export async function fetchOnboardingStatus(
    api: KyInstance,
): Promise<OnboardingStatus> {
    return onboardingStatusSchema.parse(
        await api.get('speed/onboarding/status').json(),
    );
}

export async function redeemOnboardingVoucher(
    api: KyInstance,
    code: string,
): Promise<OnboardingStatus> {
    return onboardingStatusSchema.parse(
        await api.post('speed/onboarding/redeem', { json: { code } }).json(),
    );
}

export async function confirmOnboardingReady(
    api: KyInstance,
): Promise<OnboardingStatus> {
    return onboardingStatusSchema.parse(
        await api.post('speed/onboarding/ready').json(),
    );
}

export async function rejoinOnboardingQueue(
    api: KyInstance,
): Promise<OnboardingStatus> {
    return onboardingStatusSchema.parse(
        await api.post('speed/onboarding/rejoin').json(),
    );
}

// Starts the deployment and returns right away. Creating the wallet is a queued
// service-wallet transaction, so completion arrives through the queue status
// poll rather than through this response.
//
// Wallet-only users have no OAuth cookie to authorize the create, so they send
// their self-signed HyperEVM-manager statement instead. `anchorChainId` tells the
// backend which anchor's identity the new trading wallet belongs to — the
// statement's own `domain.chainId` carries the same value.
export async function createOnboardingWallet(
    api: KyInstance,
    body?: {
        tradingSessionProof?: SelfSignedTradingSessionProof;
        anchorChainId?: AnchorChainId;
    },
): Promise<OnboardingWalletStart> {
    const json = {
        ...(body?.tradingSessionProof
            ? { tradingSessionProof: body.tradingSessionProof }
            : {}),
        anchorChainId: body?.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID,
    };
    return onboardingWalletStartSchema.parse(
        await api.post('speed/onboarding/wallet', { json }).json(),
    );
}
